import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  MessageSquare, Clock, CheckCircle, AlertCircle, XCircle, 
  ChevronRight, User, Headphones, ArrowLeft, Send, Filter,
  RefreshCw, Search, FileCheck, Lock, ArrowUpRight, Users,
  Phone, CreditCard, Mail, Calendar, Shield, Ticket
} from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const CATEGORIES = [
  { value: 'payment', label: 'Payment Issue', icon: '💳' },
  { value: 'ticket', label: 'Ticket Problem', icon: '🎫' },
  { value: 'event', label: 'Event Related', icon: '📅' },
  { value: 'account', label: 'Account Issue', icon: '👤' },
  { value: 'technical', label: 'Technical Problem', icon: '🔧' },
  { value: 'other', label: 'Other', icon: '📝' },
];

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-yellow-500', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500', icon: AlertCircle },
  resolved: { label: 'Resolved', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-500', icon: XCircle },
  escalated: { label: 'Escalated', color: 'bg-orange-500', icon: AlertCircle },
};

const RESOLUTION_TYPES = [
  { value: 'resolved', label: 'Issue Resolved', description: 'The issue has been successfully resolved' },
  { value: 'duplicate', label: 'Duplicate', description: 'This is a duplicate of another ticket' },
  { value: 'invalid', label: 'Invalid/Spam', description: 'The ticket is invalid or spam' },
  { value: 'no_response', label: 'No Response', description: 'User did not respond to follow-up' },
];

export default function SupportDashboard() {
  const { user } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailDialog, setDetailDialog] = useState({ open: false, complaint: null });
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [closeDialog, setCloseDialog] = useState({ open: false, complaint: null });
  const [closeForm, setCloseForm] = useState({
    resolution_type: 'resolved',
    resolution_summary: ''
  });
  const [escalateDialog, setEscalateDialog] = useState({ open: false, complaint: null });
  const [escalationReason, setEscalationReason] = useState('');
  
  // Users tab state
  const [activeTab, setActiveTab] = useState('tickets');
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [userDetailDialog, setUserDetailDialog] = useState({ open: false, user: null });

  useEffect(() => {
    fetchData();
  }, [statusFilter, categoryFilter]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  const fetchUsers = async (searchTerm = '') => {
    setUsersLoading(true);
    try {
      const params = searchTerm ? `?query=${encodeURIComponent(searchTerm)}` : '';
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users/search${params}`),
        axios.get(`${API_URL}/api/admin/users/stats`)
      ]);
      setUsers(usersRes.data);
      setUserStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUserSearch = (e) => {
    e.preventDefault();
    fetchUsers(userSearchQuery);
  };

  const openUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/${userId}`);
      setUserDetailDialog({ open: true, user: res.data });
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);

      const [complaintsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/complaints?${params}`),
        axios.get(`${API_URL}/api/admin/complaints/stats`)
      ]);
      setComplaints(complaintsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Support admin privileges required.');
      } else {
        toast.error('Failed to load support tickets');
      }
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (complaint) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/complaints/${complaint.complaint_id}`,
        
      );
      setDetailDialog({ open: true, complaint: response.data });
    } catch (error) {
      toast.error('Failed to load complaint details');
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/complaints/${detailDialog.complaint.complaint_id}/respond`,
        { message: replyMessage },
        
      );
      toast.success('Reply sent!');
      setReplyMessage('');
      
      // Refresh complaint detail
      const response = await axios.get(
        `${API_URL}/api/complaints/${detailDialog.complaint.complaint_id}`,
        
      );
      setDetailDialog({ open: true, complaint: response.data });
      fetchData();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseCase = async () => {
    if (!closeForm.resolution_summary.trim()) {
      toast.error('Please provide a resolution summary');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/complaints/${closeDialog.complaint.complaint_id}/close`,
        closeForm,
        
      );
      toast.success('Case closed successfully');
      setCloseDialog({ open: false, complaint: null });
      setCloseForm({ resolution_type: 'resolved', resolution_summary: '' });
      setDetailDialog({ open: false, complaint: null });
      fetchData();
    } catch (error) {
      console.error('Error closing case:', error);
      toast.error(error.response?.data?.detail || 'Failed to close case');
    } finally {
      setSubmitting(false);
    }
  };

  const openCloseDialog = (complaint) => {
    setCloseDialog({ open: true, complaint });
    setCloseForm({ resolution_type: 'resolved', resolution_summary: '' });
  };

  const openEscalateDialog = (complaint) => {
    setEscalateDialog({ open: true, complaint });
    setEscalationReason('');
  };

  const handleEscalate = async () => {
    if (!escalationReason.trim()) {
      toast.error('Please provide a reason for escalation');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/complaints/${escalateDialog.complaint.complaint_id}/escalate`,
        { 
          escalation_reason: escalationReason,
          escalate_to: 'finance'
        },
        
      );
      toast.success('Complaint escalated to Finance department');
      setEscalateDialog({ open: false, complaint: null });
      setEscalationReason('');
      setDetailDialog({ open: false, complaint: null });
      fetchData();
    } catch (error) {
      console.error('Error escalating:', error);
      toast.error(error.response?.data?.detail || 'Failed to escalate complaint');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/complaints/${detailDialog.complaint.complaint_id}/status`,
        { status: newStatus },
        
      );
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
      
      // Refresh
      const response = await axios.get(
        `${API_URL}/api/complaints/${detailDialog.complaint.complaint_id}`,
        
      );
      setDetailDialog({ open: true, complaint: response.data });
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.open;
  const getCategoryLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || cat;
  const getCategoryIcon = (cat) => CATEGORIES.find(c => c.value === cat)?.icon || '📝';

  const filteredComplaints = complaints.filter(c => {
    if (!searchQuery) return true;
    return c.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
           c.user_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
           c.user_name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10">
              <Headphones className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold">Support Dashboard</h1>
                <Badge className="bg-blue-500">Support Admin</Badge>
              </div>
              <p className="text-muted-foreground">Manage user complaints, support tickets, and user lookup</p>
            </div>
          </div>
          <Button variant="outline" onClick={activeTab === 'users' ? () => fetchUsers(userSearchQuery) : fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="tickets" className="gap-2" data-testid="support-tab-tickets">
              <MessageSquare className="w-4 h-4" />
              Support Tickets
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2" data-testid="support-tab-users">
              <Users className="w-4 h-4" />
              User Lookup
            </TabsTrigger>
          </TabsList>

          {/* Support Tickets Tab */}
          <TabsContent value="tickets" className="space-y-6">
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="border-l-4 border-l-yellow-500">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Clock className="w-8 h-8 text-yellow-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats.open}</p>
                        <p className="text-sm text-muted-foreground">Open</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-8 h-8 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats.in_progress}</p>
                        <p className="text-sm text-muted-foreground">In Progress</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-orange-500">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <ArrowUpRight className="w-8 h-8 text-orange-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats.escalated || 0}</p>
                        <p className="text-sm text-muted-foreground">Escalated</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-8 h-8 text-green-500" />
                      <div>
                    <p className="text-2xl font-bold">{stats.resolved}</p>
                    <p className="text-sm text-muted-foreground">Resolved</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-gray-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-8 h-8 text-gray-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats.total}</p>
                    <p className="text-sm text-muted-foreground">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by subject, user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="escalated">Escalated</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.icon} {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Complaints List */}
        <Card>
          <CardHeader>
            <CardTitle>Support Tickets ({filteredComplaints.length})</CardTitle>
            <CardDescription>Click on a ticket to view details and respond</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-20 skeleton rounded-lg" />
                ))}
              </div>
            ) : filteredComplaints.length > 0 ? (
              <div className="space-y-3">
                {filteredComplaints.map((complaint) => {
                  const statusConfig = getStatusConfig(complaint.status);
                  const StatusIcon = statusConfig.icon;
                  return (
                    <div
                      key={complaint.complaint_id}
                      className="flex items-start gap-4 p-4 rounded-xl border hover:border-primary/50 cursor-pointer transition-colors"
                      onClick={() => openDetail(complaint)}
                      data-testid={`support-ticket-${complaint.complaint_id}`}
                    >
                      <div className="text-2xl">{getCategoryIcon(complaint.category)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-semibold truncate">{complaint.subject}</h4>
                          <Badge className={`${statusConfig.color} text-white text-xs`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">{complaint.message}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                          <span className="font-medium">{complaint.user_name}</span>
                          <span>({complaint.user_email})</span>
                          <span>•</span>
                          <span>{getCategoryLabel(complaint.category)}</span>
                          <span>•</span>
                          <span>{format(new Date(complaint.created_at), 'MMM d, h:mm a')}</span>
                          {complaint.responses?.length > 0 && (
                            <>
                              <span>•</span>
                              <span className="text-primary">{complaint.responses.length} responses</span>
                            </>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No tickets found</h3>
                <p className="text-muted-foreground">
                  {statusFilter !== 'all' || categoryFilter !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'No support tickets have been submitted yet.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            {/* User Stats */}
            {userStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-primary">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Users className="w-8 h-8 text-primary" />
                      <div>
                        <p className="text-2xl font-bold">{userStats.total}</p>
                        <p className="text-sm text-muted-foreground">Total Users</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <User className="w-8 h-8 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{userStats.attendees}</p>
                        <p className="text-sm text-muted-foreground">Attendees</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-8 h-8 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{userStats.organizers}</p>
                        <p className="text-sm text-muted-foreground">Organizers</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Shield className="w-8 h-8 text-purple-500" />
                      <div>
                        <p className="text-2xl font-bold">{userStats.with_2fa}</p>
                        <p className="text-sm text-muted-foreground">With 2FA</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* User Search */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  User Lookup
                </CardTitle>
                <CardDescription>Search users by username, name, or email</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleUserSearch} className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by username, first name, last name, or email..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="user-search-input"
                    />
                  </div>
                  <Button type="submit" data-testid="user-search-btn">
                    Search
                  </Button>
                </form>

                {usersLoading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-20 skeleton rounded-xl" />
                    ))}
                  </div>
                ) : users.length > 0 ? (
                  <div className="space-y-3">
                    {users.map((u) => (
                      <div
                        key={u.user_id}
                        className="flex items-center gap-4 p-4 rounded-xl border hover:border-primary/50 cursor-pointer transition-colors"
                        onClick={() => openUserDetail(u.user_id)}
                        data-testid={`user-row-${u.user_id}`}
                      >
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <User className="w-6 h-6 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold">
                              {u.first_name && u.last_name 
                                ? `${u.first_name} ${u.last_name}` 
                                : u.name}
                            </h4>
                            {u.username && (
                              <span className="text-sm text-muted-foreground">@{u.username}</span>
                            )}
                            <Badge variant="outline" className="capitalize">{u.role}</Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {u.email}
                            </span>
                            {u.has_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                ***-***-{u.phone_last4}
                              </span>
                            )}
                            {u.card_last4 && (
                              <span className="flex items-center gap-1">
                                <CreditCard className="w-3 h-3" />
                                {u.card_brand || 'Card'} ****{u.card_last4}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      {userSearchQuery ? 'No users found' : 'Search for users'}
                    </h3>
                    <p className="text-muted-foreground">
                      {userSearchQuery 
                        ? 'Try a different search term' 
                        : 'Enter a username, name, or email to search'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* User Detail Dialog */}
        <Dialog open={userDetailDialog.open} onOpenChange={(open) => !open && setUserDetailDialog({ open: false, user: null })}>
          <DialogContent className="sm:max-w-2xl">
            {userDetailDialog.user && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      {userDetailDialog.user.picture ? (
                        <img src={userDetailDialog.user.picture} alt={userDetailDialog.user.name} className="w-16 h-16 rounded-full object-cover" />
                      ) : (
                        <User className="w-8 h-8 text-primary" />
                      )}
                    </div>
                    <div>
                      <DialogTitle className="text-xl">
                        {userDetailDialog.user.first_name && userDetailDialog.user.last_name 
                          ? `${userDetailDialog.user.first_name} ${userDetailDialog.user.last_name}` 
                          : userDetailDialog.user.name}
                      </DialogTitle>
                      {userDetailDialog.user.username && (
                        <p className="text-muted-foreground">@{userDetailDialog.user.username}</p>
                      )}
                      <Badge variant="outline" className="capitalize mt-1">{userDetailDialog.user.role}</Badge>
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-6 py-4">
                  {/* Contact Info */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase">Contact Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <Mail className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Email</p>
                          <p className="font-medium">{userDetailDialog.user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <Phone className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Phone (Last 4)</p>
                          <p className="font-medium">
                            {userDetailDialog.user.phone_masked || 'Not provided'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Info */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase">Payment Information</h4>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <CreditCard className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Card on File</p>
                        <p className="font-medium">
                          {userDetailDialog.user.card_last4 
                            ? `${userDetailDialog.user.card_brand || 'Card'} ending in ${userDetailDialog.user.card_last4}` 
                            : 'No card on file'}
                        </p>
                        {userDetailDialog.user.card_exp && (
                          <p className="text-xs text-muted-foreground">Exp: {userDetailDialog.user.card_exp}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Activity Stats */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase">Activity</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 rounded-lg bg-muted/50">
                        <Ticket className="w-5 h-5 mx-auto mb-1 text-primary" />
                        <p className="text-xl font-bold">{userDetailDialog.user.tickets_count}</p>
                        <p className="text-xs text-muted-foreground">Tickets</p>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-muted/50">
                        <MessageSquare className="w-5 h-5 mx-auto mb-1 text-orange-500" />
                        <p className="text-xl font-bold">{userDetailDialog.user.complaints_count}</p>
                        <p className="text-xs text-muted-foreground">Complaints</p>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-muted/50">
                        <Shield className="w-5 h-5 mx-auto mb-1 text-green-500" />
                        <p className="text-xl font-bold">{userDetailDialog.user.two_fa_enabled ? 'Yes' : 'No'}</p>
                        <p className="text-xs text-muted-foreground">2FA Enabled</p>
                      </div>
                    </div>
                  </div>

                  {/* Recent Transactions */}
                  {userDetailDialog.user.recent_transactions?.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-muted-foreground uppercase">Recent Transactions</h4>
                      <div className="space-y-2">
                        {userDetailDialog.user.recent_transactions.map((txn) => (
                          <div key={txn.transaction_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                            <div>
                              <p className="font-medium text-sm">{txn.event_title}</p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(txn.created_at), 'MMM d, yyyy h:mm a')}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">₦{txn.amount?.toLocaleString()}</p>
                              <Badge variant="outline" className="text-xs capitalize">{txn.status}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Account Info */}
                  <div className="flex items-center justify-between text-sm text-muted-foreground pt-4 border-t">
                    <span>User ID: {userDetailDialog.user.user_id}</span>
                    <span>Joined: {format(new Date(userDetailDialog.user.created_at), 'MMM d, yyyy')}</span>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Ticket Detail Dialog */}
        <Dialog open={detailDialog.open} onOpenChange={(open) => !open && setDetailDialog({ open: false, complaint: null })}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            {detailDialog.complaint && (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="text-3xl">{getCategoryIcon(detailDialog.complaint.category)}</div>
                      <div>
                        <DialogTitle className="text-xl">{detailDialog.complaint.subject}</DialogTitle>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge className={`${getStatusConfig(detailDialog.complaint.status).color} text-white`}>
                            {getStatusConfig(detailDialog.complaint.status).label}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {getCategoryLabel(detailDialog.complaint.category)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Status Update Dropdown */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select 
                        value={detailDialog.complaint.status} 
                        onValueChange={updateStatus}
                        disabled={submitting || detailDialog.complaint.status === 'closed' || detailDialog.complaint.status === 'escalated'}
                      >
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="escalated" disabled>Escalated</SelectItem>
                          <SelectItem value="closed" disabled>Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {/* Escalate to Finance button - show for payment-related or if support needs finance help */}
                      {!detailDialog.complaint.escalated_to && 
                       detailDialog.complaint.status !== 'closed' && 
                       detailDialog.complaint.status !== 'escalated' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEscalateDialog(detailDialog.complaint)}
                          className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                          data-testid="escalate-btn"
                        >
                          <ArrowUpRight className="w-4 h-4 mr-1" />
                          Escalate to Finance
                        </Button>
                      )}
                      
                      {detailDialog.complaint.status !== 'closed' && detailDialog.complaint.status !== 'escalated' && (
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => openCloseDialog(detailDialog.complaint)}
                          data-testid="close-case-btn"
                        >
                          <Lock className="w-4 h-4 mr-1" />
                          Close Case
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Escalation Info Banner */}
                  {detailDialog.complaint.escalated_to && (
                    <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/20">
                      <ArrowUpRight className="w-4 h-4 text-orange-500" />
                      <span className="text-sm text-orange-500">
                        Escalated to {detailDialog.complaint.escalated_to.toUpperCase()} on{' '}
                        {format(new Date(detailDialog.complaint.escalated_at), 'MMM d, yyyy h:mm a')}
                        {detailDialog.complaint.escalated_by_name && ` by ${detailDialog.complaint.escalated_by_name}`}
                      </span>
                    </div>
                  )}
                  
                  {/* User Info */}
                  <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    <User className="w-4 h-4" />
                    <span className="font-medium">{detailDialog.complaint.user_name}</span>
                    <span>({detailDialog.complaint.user_email})</span>
                    <span>•</span>
                    <span>{format(new Date(detailDialog.complaint.created_at), 'MMM d, yyyy h:mm a')}</span>
                  </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto py-4 space-y-4">
                  {/* Original Message */}
                  <div className="p-4 rounded-xl bg-muted/50">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4" />
                      <span className="font-medium">{detailDialog.complaint.user_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(detailDialog.complaint.created_at), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{detailDialog.complaint.message}</p>
                  </div>

                  {/* Responses */}
                  {detailDialog.complaint.responses?.map((response) => (
                    <div 
                      key={response.response_id}
                      className={`p-4 rounded-xl ${response.is_admin ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50'}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {response.is_admin ? (
                          <Headphones className="w-4 h-4 text-primary" />
                        ) : (
                          <User className="w-4 h-4" />
                        )}
                        <span className="font-medium">
                          {response.is_admin ? response.user_name : detailDialog.complaint.user_name}
                        </span>
                        {response.is_admin && (
                          <Badge variant="outline" className="text-xs">Support</Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(response.created_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{response.message}</p>
                    </div>
                  ))}
                </div>

                {/* Reply Section */}
                {detailDialog.complaint.status !== 'closed' && (
                  <div className="border-t pt-4">
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Type your response to the customer..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        rows={3}
                        className="flex-1"
                        data-testid="support-reply-input"
                      />
                      <Button 
                        onClick={handleReply} 
                        disabled={submitting || !replyMessage.trim()}
                        className="self-end"
                        data-testid="support-send-reply-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Send
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Close Case Dialog */}
        <Dialog open={closeDialog.open} onOpenChange={(open) => !open && setCloseDialog({ open: false, complaint: null })}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-green-500" />
                Close Support Case
              </DialogTitle>
              <DialogDescription>
                Closing this case will mark it as resolved and notify the user. Please provide a resolution summary.
              </DialogDescription>
            </DialogHeader>

            {closeDialog.complaint && (
              <div className="space-y-4 py-4">
                {/* Case Summary */}
                <div className="p-3 rounded-lg bg-muted/50 text-sm">
                  <p className="font-medium">{closeDialog.complaint.subject}</p>
                  <p className="text-muted-foreground mt-1">
                    From: {closeDialog.complaint.user_name} ({closeDialog.complaint.user_email})
                  </p>
                </div>

                {/* Resolution Type */}
                <div className="space-y-2">
                  <Label>Resolution Type *</Label>
                  <Select
                    value={closeForm.resolution_type}
                    onValueChange={(value) => setCloseForm({ ...closeForm, resolution_type: value })}
                  >
                    <SelectTrigger data-testid="resolution-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOLUTION_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div>
                            <span className="font-medium">{type.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {RESOLUTION_TYPES.find(t => t.value === closeForm.resolution_type)?.description}
                  </p>
                </div>

                {/* Resolution Summary */}
                <div className="space-y-2">
                  <Label htmlFor="resolution-summary">Resolution Summary *</Label>
                  <Textarea
                    id="resolution-summary"
                    placeholder="Describe how the issue was resolved..."
                    value={closeForm.resolution_summary}
                    onChange={(e) => setCloseForm({ ...closeForm, resolution_summary: e.target.value })}
                    rows={4}
                    data-testid="resolution-summary-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    This summary will be visible to the user.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setCloseDialog({ open: false, complaint: null })}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCloseCase} 
                disabled={submitting || !closeForm.resolution_summary.trim()}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-close-case-btn"
              >
                {submitting ? 'Closing...' : 'Close Case'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Escalate Dialog */}
        <Dialog open={escalateDialog.open} onOpenChange={(open) => !open && setEscalateDialog({ open: false, complaint: null })}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowUpRight className="w-5 h-5 text-orange-500" />
                Escalate to Finance Department
              </DialogTitle>
              <DialogDescription>
                This will escalate the complaint to the finance team for handling financial-related issues such as refunds, payment disputes, or billing problems.
              </DialogDescription>
            </DialogHeader>

            {escalateDialog.complaint && (
              <div className="space-y-4 py-4">
                {/* Case Summary */}
                <div className="p-3 rounded-lg bg-muted/50 text-sm">
                  <p className="font-medium">{escalateDialog.complaint.subject}</p>
                  <p className="text-muted-foreground mt-1">
                    From: {escalateDialog.complaint.user_name} ({escalateDialog.complaint.user_email})
                  </p>
                  <p className="text-muted-foreground mt-1">
                    Category: {getCategoryLabel(escalateDialog.complaint.category)}
                  </p>
                </div>

                {/* Escalation Reason */}
                <div className="space-y-2">
                  <Label htmlFor="escalation-reason">Reason for Escalation *</Label>
                  <Textarea
                    id="escalation-reason"
                    placeholder="Explain why this needs to be handled by the finance department..."
                    value={escalationReason}
                    onChange={(e) => setEscalationReason(e.target.value)}
                    rows={4}
                    data-testid="escalation-reason-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    This reason will be visible to the finance team.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setEscalateDialog({ open: false, complaint: null })}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleEscalate} 
                disabled={submitting || !escalationReason.trim()}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="confirm-escalate-btn"
              >
                {submitting ? 'Escalating...' : 'Escalate to Finance'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
